/** The Map's places (redesign phase 6): stations or sensors, each with a
 *  status, a position when one is known, the line under its name and the
 *  links out. Statuses are the api's verdicts (systemStatus for a station,
 *  the sensor's own for a sensor), raised to Alarm or Warning by the open
 *  rule events on the place (workspace v5); nothing here reads the clock,
 *  which the hover summary is handed. */
import type { Alarm, CompareChannel, LatestReading, Sensor, Station } from '../api/types'
import { VENDOR_LABEL } from '../api/types'
import { alarmStations } from './alerts'
import { MONITORING_DELAYED, systemStatus } from './freshness'
import type { Observation } from './observations'
import { searchTerms } from './stationSearch'
import { relativeAge } from './time'

export type Layer = 'stations' | 'sensors'
export type PlaceStatus = 'receiving' | 'no_data' | 'not_checked'
export const NO_TYPE = 'Type not set'

export interface Place {
  /** `station:<code>` or `sensor:<code>:<key>`, unique across layers. */
  id: string
  layer: Layer
  /** The sensor's key (an instrument id, or `logger`); null for a station. */
  key: string | null
  /** The gateway node a sensor is; null for a logger and for a station. */
  instrumentId: number | null
  name: string
  sub: string
  status: PlaceStatus
  projects: string[]
  position: Sensor['position']
  elevation: number | null
  /** The sensor that gives a station its position, by name. */
  positionFrom: string | null
  type: string | null
  station: { code: string; name: string }
  readingsHref: string
  infoHref: string
  words: string[]
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

export function readingsHref(code: string, instrumentId: number | null): string {
  return instrumentId === null ? `/explore/${code}` : `/explore/${code}/${instrumentId}`
}

export function infoHref(code: string, key?: string): string {
  return key ? `/sensors/info/${code}/${encodeURIComponent(key)}` : `/sensors/info/${code}`
}

const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, 'en', { numeric: true, sensitivity: 'base' })

/** A station is where its first positioned sensor is, by name (the board
 *  puts Akhuti-2 at its piezometer): never an average of its sensors. */
export function stationPlaces(stations: Station[], sensors: Sensor[], checksStale: boolean): Place[] {
  const byStation = new Map<string, Sensor[]>()
  for (const s of sensors) {
    const list = byStation.get(s.station.code) ?? []
    list.push(s)
    byStation.set(s.station.code, list)
  }
  return stations.filter((s) => s.active).map((st) => {
    const own = [...(byStation.get(st.code) ?? [])].sort(byName)
    const placed = own.find((s) => s.position)
    const source = VENDOR_LABEL[st.vendor] ?? st.vendor
    const nodes = own.filter((s) => s.instrument_id !== null).length
    return {
      id: `station:${st.code}`, layer: 'stations' as const, key: null, instrumentId: null, name: st.name,
      sub: st.vendor === 'cmt_edge' ? `${source} · ${plural(nodes, 'sensor')}` : source,
      status: systemStatus(st, checksStale).status,
      projects: st.projects ?? [],
      position: placed?.position ?? null, elevation: null,
      positionFrom: placed && placed.instrument_id !== null ? placed.name : null,
      type: null, station: { code: st.code, name: st.name },
      readingsHref: readingsHref(st.code, null), infoHref: infoHref(st.code),
      words: searchTerms([st.name, st.code, st.external_id, source].join(' ')),
    }
  }).sort(byName)
}

export function sensorPlaces(sensors: Sensor[], checksStale: boolean): Place[] {
  return sensors.map((s) => ({
    id: `sensor:${s.station.code}:${s.key}`, layer: 'sensors' as const, key: s.key,
    instrumentId: s.instrument_id, name: s.name,
    sub: `${s.sensor_type ?? NO_TYPE} · ${s.station.name}`,
    status: checksStale ? 'not_checked' as const : s.status,
    projects: s.station.projects,
    position: s.position, elevation: s.elevation_m, positionFrom: null,
    type: s.sensor_type, station: { code: s.station.code, name: s.station.name },
    readingsHref: readingsHref(s.station.code, s.instrument_id),
    infoHref: infoHref(s.station.code, s.key),
    words: searchTerms([s.name, s.model ?? '', s.sensor_type ?? '', s.station.name,
                        s.station.code, s.instrument_id === null ? '' : String(s.instrument_id)]
      .join(' ')),
  })).sort((a, b) => byName(a.station, b.station) || byName(a, b))
}

export interface PlaceFilter {
  project: string | null
  type: string | null
  query: string
}

export function inProject(place: Place, project: string | null): boolean {
  return project === null || place.projects.includes(project)
}

/** Project, then sensor type (a sensor with none matches "Type not set"),
 *  then every word of the search in any order, as the station search does. */
export function filterPlaces(places: Place[], filter: PlaceFilter): Place[] {
  const terms = searchTerms(filter.query)
  return places.filter((p) =>
    inProject(p, filter.project)
    && (filter.type === null || (p.type ?? NO_TYPE) === filter.type)
    && terms.every((t) => p.words.some((w) => w.includes(t))))
}

/** "All projects (13 stations)", then each project the reader can see with
 *  its count on this layer, whatever the search. */
export function projectOptions(places: Place[], projects: string[], layer: Layer):
    { value: string | null; label: string }[] {
  const noun = layer === 'stations' ? 'station' : 'sensor'
  return [{ value: null, label: `All projects (${plural(places.length, noun)})` },
    ...projects.map((name) => ({
      value: name,
      label: `${name} (${plural(places.filter((p) => p.projects.includes(name)).length, noun)})`,
    }))]
}

/** The sensor types present in scope, by name, "Type not set" last. */
export function typeOptions(places: Place[]): string[] {
  const types = new Set(places.map((p) => p.type ?? NO_TYPE))
  const named = [...types].filter((t) => t !== NO_TYPE).sort((a, b) => a.localeCompare(b))
  return types.has(NO_TYPE) ? [...named, NO_TYPE] : named
}

export function countLine(shown: Place[], total: number, layer: Layer): string {
  const noun = layer === 'stations' ? 'station' : 'sensor'
  const placed = shown.filter((p) => p.position).length
  return `${shown.length} of ${plural(total, noun)} shown, ${placed} with coordinates`
}

export const STATUS_WORD: Record<PlaceStatus, string> = {
  receiving: 'Receiving', no_data: 'No data', not_checked: MONITORING_DELAYED,
}

/** The project line on the selected place's card. */
export function projectLine(place: Place): string {
  if (place.projects.length === 0) return 'In no project yet'
  return `${place.projects.length === 1 ? 'Project' : 'Projects'}: ${place.projects.join(', ')}`
}

/** The card's alarm pill: the open alarms and warnings on the place's
 *  station (alert rules, phase 4). A rule on one reading is not tied to a
 *  node, so a sensor shows its station's. */
export function alarmPill(alarms: Alarm[], station: string): { text: string; tone: 'alarm' | 'late' | 'stale' } {
  const own = alarms.filter((a) => alarmStations(a).some((s) => s.code === station))
  const alarmCount = own.filter((a) => a.level === 'alarm').length
  const warnings = own.length - alarmCount
  if (alarmCount) return { text: alarmCount === 1 ? '1 alarm' : `${alarmCount} alarms`, tone: 'alarm' }
  if (warnings) return { text: warnings === 1 ? '1 warning' : `${warnings} warnings`, tone: 'late' }
  return { text: 'Alarms: none', tone: 'stale' }
}

// -------------------------------------------- status on the map (v5)

/** A place's state on the map: an open rule event outranks transmission,
 *  Alarm before Warning, then the api's verdict. */
export type MapStatus = 'alarm' | 'warning' | PlaceStatus

export const MAP_STATUS_WORD: Record<MapStatus, string> = {
  alarm: 'Alarm', warning: 'Warning', ...STATUS_WORD,
}

/** The pill tone on this page. No data takes the stale tone here, as its
 *  marker does (lib/mapSymbols.ts, MARKER_GEOMETRY): on the map red means
 *  an alarm, and a silent sensor is told by its word and its badge. */
export const MAP_STATUS_TONE: Record<MapStatus, 'alarm' | 'warning' | 'normal' | 'stale'> = {
  alarm: 'alarm', warning: 'warning', no_data: 'stale', receiving: 'normal', not_checked: 'stale',
}

/** Which gateway node a stored channel belongs to, by station and parameter
 *  (null for a logger's channel), from the channel list Explore sensors
 *  uses: an alarm names its parameter, never its node. */
export type ChannelInstruments = ReadonlyMap<string, number | null>

export function channelInstruments(
  channels: Pick<CompareChannel, 'station' | 'parameter' | 'instrument_id'>[]): ChannelInstruments {
  return new Map(channels.map((c) => [`${c.station}|${c.parameter}`, c.instrument_id]))
}

type AlarmPlace = Pick<Place, 'id' | 'layer' | 'station' | 'instrumentId'>

/** The places an open alarm marks, out of those at its station. A station
 *  holds every one at it. On the sensors layer an alarm on a channel marks
 *  the sensor that channel belongs to: its node, or the logger for a
 *  channel with none. One on the whole station, on a channel whose node is
 *  not known (the channel list not loaded yet, or not listing it), or on a
 *  node with no sensor in the list, marks every sensor at the station: a
 *  sensor in alarm must never look quiet because the lookup was missing. */
export function alarmPlaces<P extends AlarmPlace>(alarm: Alarm, here: P[],
                                                  instruments?: ChannelInstruments): P[] {
  const key = alarm.parameter && alarm.station ? `${alarm.station.code}|${alarm.parameter.code}` : null
  if (key === null || !instruments?.has(key)) return here
  const node = instruments.get(key)
  const mine = here.filter((p) => p.layer === 'sensors' && p.instrumentId === node)
  return mine.length ? mine : here
}

const byLevelThenAge = (x: Alarm, y: Alarm) =>
  x.level === y.level ? x.since.localeCompare(y.since) : x.level === 'alarm' ? -1 : 1

export function mapStatus(place: Pick<Place, 'status'>, own: Alarm[]): MapStatus {
  if (own.some((a) => a.level === 'alarm')) return 'alarm'
  if (own.some((a) => a.level === 'warning')) return 'warning'
  return place.status
}

/** The states beside a marker: its own, and how it is transmitting when an
 *  alarm outranks that, since a sensor can be in alarm and silent at once. */
export function statusParts(place: Pick<Place, 'status'>, status: MapStatus): MapStatus[] {
  return status === 'alarm' || status === 'warning' ? [status, place.status] : [status]
}

export function statusWords(place: Pick<Place, 'status'>, status: MapStatus): string[] {
  return statusParts(place, status).map((s) => MAP_STATUS_WORD[s])
}

export interface PlaceState { status: MapStatus; alarms: Alarm[] }

/** Every place's state and its own open alarms, alarms before warnings,
 *  the oldest first. Handed the whole layer, never a filtered view: whether
 *  a node has a sensor in the list must not depend on the search. */
export function placeStates(places: (AlarmPlace & Pick<Place, 'status'>)[], alarms: Alarm[],
                            instruments?: ChannelInstruments): Map<string, PlaceState> {
  const atStation = new Map<string, (AlarmPlace & Pick<Place, 'status'>)[]>()
  for (const p of places) atStation.set(p.station.code, [...(atStation.get(p.station.code) ?? []), p])
  const own = new Map<string, Alarm[]>()
  for (const alarm of alarms) {
    // A strategy's alarm marks every station it reads.
    for (const station of alarmStations(alarm)) {
      for (const p of alarmPlaces(alarm, atStation.get(station.code) ?? [], instruments)) {
        own.set(p.id, [...(own.get(p.id) ?? []), alarm])
      }
    }
  }
  return new Map(places.map((p) => {
    const mine = (own.get(p.id) ?? []).sort(byLevelThenAge)
    return [p.id, { status: mapStatus(p, mine), alarms: mine }]
  }))
}

/** The card's alarm pill, counting the place's own open alarms: all of its
 *  station's for a station, and for a sensor those placeStates gave it, so
 *  the pill agrees with the marker. Warnings take the warning tone the
 *  marker's ring and the Warning pill beside it use, not the board's late
 *  tone, so one state is one colour on this page. The station's others are
 *  a line of their own (otherAlarmsLine). */
export function ownAlarmPill(state: PlaceState | undefined): { text: string; tone: 'alarm' | 'warning' | 'stale' } {
  const alarms = state?.alarms ?? []
  const pill = alarmPill(alarms, alarms[0] ? alarmStations(alarms[0])[0]?.code ?? '' : '')
  return { text: pill.text, tone: pill.tone === 'late' ? 'warning' : pill.tone }
}

/** Said under a sensor whose station has open alarms on its other sensors:
 *  the marker is quiet, the station is not. */
export function otherAlarmsLine(place: Pick<Place, 'layer' | 'station'>, state: PlaceState | undefined,
                                alarms: Alarm[]): string | null {
  if (place.layer === 'stations') return null
  const mine = new Set((state?.alarms ?? []).map((a) => a.id))
  const others = alarms.filter((a) => alarmStations(a).some((s) => s.code === place.station.code) && !mine.has(a.id)).length
  if (!others) return null
  return `${place.station.name} has ${others === 1 ? '1 more open alarm' : `${others} more open alarms`} on its other sensors.`
}

export type StatusFilter = 'all' | 'alarm' | 'warning' | 'no_data'
const STATUS_FILTERS: StatusFilter[] = ['all', 'alarm', 'warning', 'no_data']

export function parseStatusFilter(value: string | null): StatusFilter {
  return STATUS_FILTERS.find((f) => f === value) ?? 'all'
}

/** Alarm and Warning are the marker's state; No data is the place's
 *  transmission whatever outranks it on the marker, so a silent sensor in
 *  alarm is in both lists. */
export function matchesStatus(place: Place, state: PlaceState | undefined, filter: StatusFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'no_data') return place.status === 'no_data'
  return state?.status === filter
}

export function filterByStatus(places: Place[], states: ReadonlyMap<string, PlaceState>,
                               filter: StatusFilter): Place[] {
  return places.filter((p) => matchesStatus(p, states.get(p.id), filter))
}

/** The list's order, the loudest first, as the map draws them on top
 *  (lib/mapSymbols markerSortKey): an alarm, then a place that has stopped
 *  transmitting, then a warning, then the quiet ones. */
const STATUS_RANK: Record<MapStatus, number> = {
  alarm: 0, no_data: 1, warning: 2, receiving: 3, not_checked: 4,
}

export function statusRank(status: MapStatus): number {
  return STATUS_RANK[status]
}

/** The places with the loud states first, so an alarm far down a long
 *  layer is not missed; within a state the order they came in (by name,
 *  a sensor by its station first). A new array; the sort is stable. */
export function orderByStatus(places: Place[], states: ReadonlyMap<string, PlaceState>): Place[] {
  const rank = (p: Place) => statusRank(states.get(p.id)?.status ?? p.status)
  return [...places].sort((a, b) => rank(a) - rank(b))
}

/** "All statuses (12)", then each status with its count in the view. */
export function statusFilterOptions(places: Place[], states: ReadonlyMap<string, PlaceState>):
    { value: StatusFilter; label: string }[] {
  const label = { all: 'All statuses', alarm: 'Alarm', warning: 'Warning', no_data: 'No data' }
  return STATUS_FILTERS.map((value) => ({
    value, label: `${label[value]} (${filterByStatus(places, states, value).length})`,
  }))
}

/** What the hover says when markers sit on one another: the list reaches
 *  the ones beneath. */
export function stackedLine(others: number): string | null {
  if (others <= 0) return null
  return others === 1 ? '1 more marker here. Pick it in the list.'
    : `${others} more markers here. Pick one in the list.`
}

// ------------------------------------------ latest observations summary

export const SUMMARY_LIMIT = 6

export interface SummaryRow {
  name: string
  /** The stored value at its recorded precision (lib/observations). */
  value: string
  exact: string
  unit: string
  ts: string | null
  age: string
}

export interface ObservationSummary { rows: SummaryRow[]; more: number; moreText: string | null }

/** The hover's latest observations: the newest first (a station's channels
 *  arrive on different cadences, and the newest says most about whether it
 *  is transmitting), at most `limit`, then how many were left out. Values
 *  are the stored ones as Sensor info shows them; nothing is averaged. */
export function observationSummary(observations: Observation[], now: Date,
                                   limit = SUMMARY_LIMIT): ObservationSummary {
  const at = (o: Observation) => (o.ts ? Date.parse(o.ts) : Number.NaN)
  const ordered = [...observations].sort((a, b) => {
    const [x, y] = [at(a), at(b)]
    if (Number.isNaN(x) || Number.isNaN(y)) return Number(Number.isNaN(x)) - Number(Number.isNaN(y))
    return y - x
  })
  const rows = ordered.slice(0, Math.max(0, limit)).map((o) => ({
    name: o.name, value: o.value, exact: o.exact, unit: o.unit, ts: o.ts,
    age: o.ts ? relativeAge(o.ts, now) : 'no time stored',
  }))
  const more = observations.length - rows.length
  return { rows, more, moreText: more > 0 ? `and ${more} more` : null }
}

/** A station's latest readings without its link telemetry: kept are the
 *  channels the channel list names, which leaves link telemetry out as
 *  Explore sensors' picker does, and a node's own summary leaves it out
 *  through its instrument detail. With the list not loaded, or naming none
 *  of the station's channels, every reading is kept: a longer summary is
 *  better than an empty one. */
export function listedReadings<R extends Pick<LatestReading, 'parameter_code'>>(
  rows: R[], code: string, instruments?: ChannelInstruments): R[] {
  if (!instruments) return rows
  const listed = rows.filter((r) => instruments.has(`${code}|${r.parameter_code}`))
  return listed.length ? listed : rows
}

/** What a place's summary is read from, which is also its cache key: a
 *  gateway node's instrument detail, as Sensor info reads it, else the
 *  station's latest (a logger's readings, or a whole station's). A station
 *  and its logger share one answer. */
export function summaryKey(place: Pick<Place, 'layer' | 'station' | 'instrumentId'>): string {
  return place.layer === 'sensors' && place.instrumentId !== null
    ? `node:${place.station.code}:${place.instrumentId}` : `latest:${place.station.code}`
}

export const SUMMARY_TTL_MS = 60_000
/** How long a failure stands before a hover asks again. */
export const SUMMARY_RETRY_MS = 10_000

/** When a shown place's summary is asked for, decided each time it is
 *  shown and at each tick of the page's clock: not while its answer is
 *  fresh, nor while the tab is hidden (the clock ticks again when it is
 *  shown, and the place is asked for then); at once when an older answer
 *  is already on screen, being renewed; else after `delayMs`, so a pointer
 *  sweeping across markers on its way elsewhere asks for none of them. */
export function summaryPlan({ fresh, hasEntry, hidden, delayMs }: {
  fresh: boolean; hasEntry: boolean; hidden: boolean; delayMs: number
}): 'skip' | number {
  if (fresh || hidden) return 'skip'
  return hasEntry ? 0 : Math.max(0, delayMs)
}

export interface SummaryEntry {
  /** When the rows arrived, by the clock the cache was handed. */
  at: number
  rows?: Observation[]
  /** Why the newest request failed; any earlier rows are kept beside it. */
  error?: string
  failedAt?: number
}

/** One answer per place, kept a minute: moving back and forth over a marker
 *  asks once a minute at most, and only for markers the reader points at or
 *  picks, never for every marker on the map. A failure is shown but is no
 *  answer, so a hover after a short pause asks again, and keeps the last
 *  good rows as the Sensor info poll does (lib/sensorDetails mergeRefresh);
 *  their ages say how old they are. A request in flight is shared. */
export function createSummaryCache(clock: () => number, ttlMs = SUMMARY_TTL_MS,
                                   retryMs = SUMMARY_RETRY_MS) {
  const entries = new Map<string, SummaryEntry>()
  const pending = new Map<string, Promise<SummaryEntry>>()
  const isFresh = (entry: SummaryEntry | undefined): entry is SummaryEntry => {
    if (!entry) return false
    if (entry.error !== undefined) return clock() - (entry.failedAt ?? 0) < retryMs
    return entry.rows !== undefined && clock() - entry.at < ttlMs
  }
  return {
    peek: (id: string): SummaryEntry | undefined => entries.get(id),
    isFresh: (id: string): boolean => isFresh(entries.get(id)),
    load(id: string, fetch: () => Promise<Observation[]>): Promise<SummaryEntry> {
      const cached = entries.get(id)
      if (isFresh(cached)) return Promise.resolve(cached)
      const asked = pending.get(id)
      if (asked) return asked
      const request = Promise.resolve().then(fetch).then(
        (rows): SummaryEntry => ({ at: clock(), rows }),
        (reason: unknown): SummaryEntry => {
          const error = reason instanceof Error ? reason.message : String(reason)
          const last = entries.get(id)
          const failedAt = clock()
          return last?.rows ? { at: last.at, rows: last.rows, error, failedAt } : { at: failedAt, error, failedAt }
        },
      ).then((entry) => {
        entries.set(id, entry)
        pending.delete(id)
        return entry
      })
      pending.set(id, request)
      return request
    },
  }
}

export type SummaryCache = ReturnType<typeof createSummaryCache>
